import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { InventoryBalanceItem, InventoryRiskItem, Permission, PrintSettings, Role } from '../types';
import { RolePermission } from '../types';
import { Link } from 'react-router-dom';
import RestaurantNotificationStatus from '../components/RestaurantNotificationStatus';
import RestaurantNotificationSettings from '../components/RestaurantNotificationSettings';
import AiAssistantSettings from '../components/AiAssistantSettings';
import OperatingGroupsSettings from '../components/OperatingGroupsSettings';
import ProducingKitchensSettings from '../components/ProducingKitchensSettings';
import { isOperatingContextReady, useOperatingContext } from '../utils/operatingContextLifecycle';

type AdminTab = 'permissions' | 'print-settings' | 'template' | 'notifications' | 'dish-analysis' | 'risk-board' | 'price-recalc' | 'google-document-ai' | 'ai-assistant' | 'structure' | 'producing-kitchens';
const SYSTEM_CHANGE_CONFIRMATION = 'CONFIRM SYSTEM CHANGE';
const printSettingsCardStyle: React.CSSProperties = {
  background: 'var(--crm-surface)',
  color: 'var(--crm-text)',
  border: '1px solid var(--crm-border)',
  padding: '20px',
  borderRadius: 'var(--crm-radius-md)',
  boxShadow: 'var(--crm-shadow-sm)',
};

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('permissions');
  const [canViewAdmin, setCanViewAdmin] = useState(false);
  const [canEditAdmin, setCanEditAdmin] = useState(false);
  const [isSystemAdministrator, setIsSystemAdministrator] = useState(false);
  const operatingContext = useOperatingContext();
  const allGroupsOverview = operatingContext.status === 'all';
  const operatingScopeVerified = isOperatingContextReady(operatingContext, operatingContext.token);
  const [accessError, setAccessError] = useState('');
  const [permissionsMessage, setPermissionsMessage] = useState('');
  useEffect(() => {
    setCanViewAdmin(false);
    setCanEditAdmin(false);
    setIsSystemAdministrator(false);
    if (!operatingScopeVerified) return;
    const controller = new AbortController();
    api.get<{ permissions: string[]; editPermissions: string[]; systemAdministrator: boolean }>(
      '/users/me/access', { signal: controller.signal },
    ).then(({ data }) => {
      if (controller.signal.aborted) return;
      const { permissions, editPermissions, systemAdministrator } = data;
      setCanViewAdmin(permissions.includes('page.admin'));
      setCanEditAdmin(permissions.includes('page.admin') && editPermissions.includes('page.admin'));
      setIsSystemAdministrator(systemAdministrator === true);
      setAccessError('');
    }).catch(() => {
      if (controller.signal.aborted) return;
      setCanViewAdmin(false);
      setCanEditAdmin(false);
      setIsSystemAdministrator(false);
      setAccessError('Не вдалося перевірити доступ і робочий контекст. Редагування вимкнене; оновіть сторінку.');
    });
    return () => controller.abort();
  }, [operatingScopeVerified, operatingContext.generation]);
  const canEditAdminSettings = canEditAdmin && operatingScopeVerified && !allGroupsOverview;
  const canReadSystemSettings = canViewAdmin && isSystemAdministrator && operatingScopeVerified;
  const canEditSystemSettings = canReadSystemSettings && canEditAdminSettings;
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Record<number, RolePermission[]>>({});
  const [printSettings, setPrintSettings] = useState<PrintSettings | null>(null);
  const [savingRoleId, setSavingRoleId] = useState<number | null>(null);
  const [savingPrintSettings, setSavingPrintSettings] = useState(false);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  const [newPermission, setNewPermission] = useState({ key: '', name: '', category: '', description: '' });
  const [savingPermission, setSavingPermission] = useState(false);
  const [googleDocAiSettings, setGoogleDocAiSettings] = useState<{
    projectId: string; location: string; processorId: string;
    credentialConfigured: boolean; credentialSource: string; statusMessage: string;
  } | null>(null);
  const [googleDocAiForm, setGoogleDocAiForm] = useState({ projectId: '', location: '', processorId: '', credentialJson: '' });
  const [savingGoogleDocAi, setSavingGoogleDocAi] = useState(false);
  const [googleDocAiError, setGoogleDocAiError] = useState('');
  const [googleDocAiMessage, setGoogleDocAiMessage] = useState('');
  const [googleDocAiClearConfirm, setGoogleDocAiClearConfirm] = useState(false);
  const [priceRecalcLastN, setPriceRecalcLastN] = useState(3);
  const [priceRecalcPreview, setPriceRecalcPreview] = useState<any[] | null>(null);
  const [priceRecalcStatus, setPriceRecalcStatus] = useState<string | null>(null);
  const [priceRecalcLoading, setPriceRecalcLoading] = useState(false);
  const [dishAnalysis, setDishAnalysis] = useState<any>(null);
  const [riskBoard, setRiskBoard] = useState<{ items: InventoryRiskItem[]; criticalCount: number; warningCount: number; totalTracked: number; categories: string[] }>({
    items: [],
    criticalCount: 0,
    warningCount: 0,
    totalTracked: 0,
    categories: [],
  });
   
  // New role management
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [showNewRoleForm, setShowNewRoleForm] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', description: '' });
  const [savingNewRole, setSavingNewRole] = useState(false);
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [editingRole, setEditingRole] = useState({ name: '', description: '' });
  const [savingEditRole, setSavingEditRole] = useState(false);

  const updateRolePermission = (roleId: number, permissionId: number, field: keyof Omit<RolePermission, 'roleId' | 'permissionId'>, value: boolean) => {
    console.log(`[updateRolePermission] Role ${roleId}, Permission ${permissionId}, Field ${field}, Value ${value}`);
    setRolePermissions((prev) => {
      const updated = {
        ...prev,
        [roleId]: (prev[roleId] || []).map((item) => (item.permissionId === permissionId ? { ...item, [field]: value } : item)),
      };
      console.log(`[updateRolePermission] Updated state for role ${roleId}:`, updated[roleId]);
      return updated;
    });
  };

  const saveRolePermissions = async (roleId: number) => {
    console.log('[saveRolePermissions] Current rolePermissions state:', rolePermissions);
    const payload = rolePermissions[roleId] || [];
    console.log('[saveRolePermissions] Payload for role', roleId, ':', payload);
    if (!window.confirm('Підтвердити глобальну зміну прав ролі? Нові дозволи впливають на доступ користувачів із цією роллю.')) return;
    setSavingRoleId(roleId);
    try {
      console.log('Saving permissions for role', roleId, 'payload:', payload);
      const res = await api.post(`/permissions/roles/${roleId}/permissions`, payload, {
        headers: { 'X-System-Change-Confirmation': SYSTEM_CHANGE_CONFIRMATION },
      });
      console.log('Save response:', res);
      setPermissionsError(null);
      setPermissionsMessage('Права збережено. Зміни діють із наступного API-запиту й не скидаються після перезапуску.');
    } catch (err: any) {
      console.error('Error saving permissions:', err);
      setPermissionsError(err?.response?.data?.message || 'Не вдалося зберегти права.');
    } finally {
      setSavingRoleId(null);
    }
  };

  const createNewRole = async () => {
    if (!newRole.name.trim()) {
      setPermissionsError('Назва ролі обовʼязкова');
      return;
    }
    if (!window.confirm('Підтвердити глобальне створення ролі? Це системна зміна прав доступу.')) return;
    setSavingNewRole(true);
    try {
      const res = await api.post<Role>('/roles', { ...newRole, confirmation: SYSTEM_CHANGE_CONFIRMATION });
      setRoles((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name, 'uk')));
      setNewRole({ name: '', description: '' });
      setShowNewRoleForm(false);
      setSelectedRoleId(res.data.id);
      setPermissionsError(null);
    } catch (err: any) {
      setPermissionsError(err?.response?.data?.message || 'Не вдалося створити роль.');
    } finally {
      setSavingNewRole(false);
    }
  };

  const updateRole = async (roleId: number) => {
    if (!editingRole.name.trim()) {
      setPermissionsError('Назва ролі обовʼязкова');
      return;
    }
    if (!window.confirm('Підтвердити глобальну зміну ролі? Це системна зміна прав доступу.')) return;
    setSavingEditRole(true);
    try {
      await api.put(`/roles/${roleId}`, { ...editingRole, confirmation: SYSTEM_CHANGE_CONFIRMATION });
      setRoles((prev) =>
        prev.map((r) => (r.id === roleId ? { ...r, ...editingRole } : r)).sort((a, b) => a.name.localeCompare(b.name, 'uk'))
      );
      setEditingRoleId(null);
      setEditingRole({ name: '', description: '' });
      setPermissionsError(null);
    } catch (err: any) {
      setPermissionsError(err?.response?.data?.message || 'Не вдалося оновити роль.');
    } finally {
      setSavingEditRole(false);
    }
  };

  const exportDishTemplate = () => {
    void (async () => {
      try {
        const res = await api.get('/export/dish-template/excel', { responseType: 'blob' });
        const headers = res.headers as Record<string, string | undefined>;
        const blob = new Blob([res.data], {
          type: headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'dish-template.xlsx';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err: any) {
        setPermissionsError(err?.response?.data?.message || 'Не вдалося експортувати AI-шаблон.');
      }
    })();
  };

  const exportSemiFinishedTemplate = () => {
    void (async () => {
      try {
        const res = await api.get('/export/semifinished-template/excel', { responseType: 'blob' });
        const headers = res.headers as Record<string, string | undefined>;
        const blob = new Blob([res.data], {
          type: headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'semifinished-template.xlsx';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err: any) {
        setPermissionsError(err?.response?.data?.message || 'Не вдалося експортувати AI-шаблон Н/Ф.');
      }
    })();
  };

  const savePrintSettings = async () => {
    if (!printSettings) return;
    if (!window.confirm('Підтвердити зміну глобальних налаштувань друку?')) return;
    setSavingPrintSettings(true);
    try {
      await api.put('/permissions/print-settings', {
        ...printSettings,
        confirmation: SYSTEM_CHANGE_CONFIRMATION,
      });
    } finally {
      setSavingPrintSettings(false);
    }
  };

  const createPermission = async () => {
    if (!window.confirm('Підтвердити створення системного права доступу?')) return;
    setSavingPermission(true);
    try {
      const res = await api.post<Permission>('/permissions', {
        ...newPermission,
        confirmation: SYSTEM_CHANGE_CONFIRMATION,
      });
      setPermissions((prev) => [...prev, res.data].sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name)));
      setNewPermission({ key: '', name: '', category: '', description: '' });
      setPermissionsError(null);
    } catch (err: any) {
      setPermissionsError(err?.response?.data?.message || 'Не вдалося створити permission.');
    } finally {
      setSavingPermission(false);
    }
  };

  const loadGoogleDocAiSettings = async () => {
    setGoogleDocAiError('');
    try {
      const res = await api.get('/google-document-ai/settings');
      setGoogleDocAiSettings(res.data);
      setGoogleDocAiForm({
        projectId: res.data?.projectId ?? '',
        location: res.data?.location ?? '',
        processorId: res.data?.processorId ?? '',
        credentialJson: '',
      });
    } catch (err: any) {
      setGoogleDocAiSettings(null);
      setGoogleDocAiError(err?.response?.data?.message || 'Не вдалося завантажити налаштування Google Document AI.');
    }
  };

  const handleGoogleDocAiCredentialFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setGoogleDocAiForm((prev) => ({ ...prev, credentialJson: String(reader.result || '') }));
    };
    reader.onerror = () => {
      setGoogleDocAiError('Не вдалося прочитати файл облікових даних.');
    };
    reader.readAsText(file);
  };

  const saveGoogleDocAiSettings = async () => {
    if (!window.confirm('Підтвердити зміну глобальних налаштувань Google Document AI?')) return;
    setSavingGoogleDocAi(true);
    setGoogleDocAiError('');
    setGoogleDocAiMessage('');
    try {
      const formData = new FormData();
      formData.append('ProjectId', googleDocAiForm.projectId.trim());
      formData.append('Location', googleDocAiForm.location.trim());
      formData.append('ProcessorId', googleDocAiForm.processorId.trim());
      formData.append('Confirmation', SYSTEM_CHANGE_CONFIRMATION);
      if (googleDocAiForm.credentialJson.trim()) {
        formData.append('CredentialJson', googleDocAiForm.credentialJson);
      }
      const res = await api.put('/google-document-ai/settings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setGoogleDocAiMessage(res.data?.message || 'Налаштування збережено.');
      setGoogleDocAiSettings(res.data?.settings ?? null);
      setGoogleDocAiForm((prev) => ({ ...prev, credentialJson: '' }));
    } catch (err: any) {
      setGoogleDocAiError(err?.response?.data?.message || 'Не вдалося зберегти налаштування Google Document AI.');
    } finally {
      setSavingGoogleDocAi(false);
    }
  };

  const clearGoogleDocAiCredential = async () => {
    setSavingGoogleDocAi(true);
    setGoogleDocAiError('');
    setGoogleDocAiMessage('');
    try {
      const formData = new FormData();
      formData.append('ProjectId', googleDocAiForm.projectId.trim());
      formData.append('Location', googleDocAiForm.location.trim());
      formData.append('ProcessorId', googleDocAiForm.processorId.trim());
      formData.append('ClearCredential', 'true');
      formData.append('Confirmation', SYSTEM_CHANGE_CONFIRMATION);
      const res = await api.put('/google-document-ai/settings', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setGoogleDocAiMessage(res.data?.message || 'Облікові дані видалено.');
      setGoogleDocAiSettings(res.data?.settings ?? null);
      setGoogleDocAiForm((prev) => ({ ...prev, credentialJson: '' }));
      setGoogleDocAiClearConfirm(false);
    } catch (err: any) {
      setGoogleDocAiError(err?.response?.data?.message || 'Не вдалося видалити облікові дані Google Document AI.');
    } finally {
      setSavingGoogleDocAi(false);
    }
  };

  const loadPriceRecalcPreview = async () => {
    setPriceRecalcLoading(true);
    setPriceRecalcStatus(null);
    try {
      const res = await api.get('/products/recalculate-prices-preview', { params: { lastReceiptsCount: priceRecalcLastN } });
      setPriceRecalcPreview(res.data?.items ?? []);
    } catch (err: any) {
      setPriceRecalcStatus(err?.response?.data?.message || 'Помилка завантаження попереднього перегляду.');
    } finally {
      setPriceRecalcLoading(false);
    }
  };

  const applyPriceRecalc = async () => {
    if (!window.confirm(`Оновити ціни ${(priceRecalcPreview ?? []).filter((i: any) => i.hasChange).length} товарів на основі ${priceRecalcLastN} останніх накладних? Ці зміни не можна скасувати автоматично.`)) return;
    setPriceRecalcLoading(true);
    setPriceRecalcStatus(null);
    try {
      const res = await api.post('/products/recalculate-prices-apply', { lastReceiptsCount: priceRecalcLastN });
      setPriceRecalcStatus(`✅ ${res.data?.message}`);
      setPriceRecalcPreview(null);
    } catch (err: any) {
      setPriceRecalcStatus(err?.response?.data?.message || 'Помилка застосування перерахунку.');
    } finally {
      setPriceRecalcLoading(false);
    }
  };

  const loadDishAnalysis = async () => {
    try {
      const dishesRes = await api.get('/dishes');
      const dishes = Array.isArray(dishesRes.data) ? dishesRes.data : [];
      const items = dishes.map((dish: any) => {
        const cost = Number(dish.costPrice ?? 0);
        const price = Number(dish.finalPrice ?? 0);
        const marginPct = price > 0 ? ((price - cost) / price) * 100 : 0;
        return { ...dish, cost, price, marginPct };
      });

      const avgMargin = items.length ? items.reduce((sum: number, item: any) => sum + item.marginPct, 0) / items.length : 0;
      const lowMargin = items.filter((item: any) => item.marginPct < 15).slice(0, 5);
      const expensive = items.filter((item: any) => item.cost > 0 && item.price > 0 && (item.cost / item.price) > 0.72).slice(0, 5);

      let guests: any[] = [];
      try {
        const guestsRes = await api.get('/guests');
        guests = Array.isArray(guestsRes.data) ? guestsRes.data : [];
      } catch {
        guests = [];
      }

      const orderResults = await Promise.all(
        guests.map(async (guest: any) => {
          try {
            const res = await api.get(`/guests/${guest.id}/orders`);
            return Array.isArray(res.data) ? res.data : [];
          } catch {
            return [];
          }
        })
      );

      const orders = orderResults.flat();
      const salesByItem = new Map<string, { name: string; revenue: number; quantity: number; orders: number }>();
      let totalRevenue = 0;
      let totalUnits = 0;
      let totalOrders = 0;

      orders.forEach((order: any) => {
        const orderItems = Array.isArray(order.orderItems) ? order.orderItems : [];
        if (orderItems.length === 0) return;
        totalOrders += 1;
        orderItems.forEach((item: any) => {
          const name = item.menuItemName ?? item.name ?? item.menuItem?.name ?? 'Невідомо';
          const quantity = Number(item.quantity ?? 0);
          const revenue = Number(item.totalPrice ?? (Number(item.unitPrice ?? 0) * quantity));
          if (!name || quantity <= 0) return;

          const current = salesByItem.get(name) ?? { name, revenue: 0, quantity: 0, orders: 0 };
          current.revenue += revenue;
          current.quantity += quantity;
          current.orders += 1;
          salesByItem.set(name, current);

          totalRevenue += revenue;
          totalUnits += quantity;
        });
      });

      const topItems = Array.from(salesByItem.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
      const weakItems = Array.from(salesByItem.values()).sort((a, b) => a.revenue - b.revenue).slice(0, 3);
      const bestItem = topItems[0] ?? null;
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      const recommendations: string[] = [];

      if (lowMargin.length > 0) {
        recommendations.push(`Зменшити маржу або підвищити ціни для: ${lowMargin.map((item: any) => item.name).join(', ')}`);
      }
      if (expensive.length > 0) {
        recommendations.push(`Перевірити собівартість для високих витрат: ${expensive.map((item: any) => item.name).join(', ')}`);
      }
      if (bestItem) {
        recommendations.push(`Найкраще продається: ${bestItem.name} — ${bestItem.quantity} шт. / ${bestItem.revenue.toFixed(0)} грн.`);
      }
      if (weakItems.length > 0) {
        recommendations.push(`Потрібно активізувати продажі: ${weakItems.map((item) => item.name).join(', ')}`);
      }
      if (items.length === 0) {
        recommendations.push('Додайте хоча б одну страву, щоб отримати рекомендації щодо калькуляції.');
      }
      if (recommendations.length === 0) {
        recommendations.push('Калькуляції в цілому збалансовані. Продовжуйте контролювати маржу при кожному оновленні складу.');
      }

      setDishAnalysis({
        total: items.length,
        avgMargin,
        lowMargin,
        expensive,
        recommendations,
        sales: {
          totalRevenue,
          totalOrders,
          totalUnits,
          avgOrderValue,
          topItems,
          weakItems,
          bestItem,
        },
      });
    } catch (err: any) {
      setDishAnalysis({
        total: 0,
        avgMargin: 0,
        lowMargin: [],
        expensive: [],
        recommendations: ['Не вдалося завантажити дані калькуляцій. Перевір, що бекенд працює і список страв доступний.'],
        sales: {
          totalRevenue: 0,
          totalOrders: 0,
          totalUnits: 0,
          avgOrderValue: 0,
          topItems: [],
          weakItems: [],
          bestItem: null,
        },
      });
    }
  };

  const loadRiskBoard = async () => {
    try {
      const res = await api.get<InventoryBalanceItem[]>('/inventorybalance');
      const items = (res.data || [])
        .filter((item) => Math.abs(item.balance) > 0 || item.stockIn > 0 || item.stockOut > 0)
        .map((item) => {
          const riskLevel: InventoryRiskItem['riskLevel'] = item.balance < 0 ? 'critical' : item.balance === 0 && item.stockOut > 0 ? 'warning' : item.balance < 5 ? 'warning' : 'normal';
          const riskLabel = riskLevel === 'critical'
            ? 'Від’ємний залишок'
            : riskLevel === 'warning'
              ? 'Потрібна перевірка'
              : 'Нормально';
          return { ...item, riskLevel, riskLabel };
        })
        .sort((a, b) => a.balance - b.balance || a.productName.localeCompare(b.productName, 'uk'));

      const criticalCount = items.filter((item) => item.riskLevel === 'critical').length;
      const warningCount = items.filter((item) => item.riskLevel === 'warning').length;
      const categories = Array.from(new Set(items.map((item) => item.categoryName || 'Без категорії'))).sort((a, b) => a.localeCompare(b, 'uk'));

      setRiskBoard({
        items: items.slice(0, 12),
        criticalCount,
        warningCount,
        totalTracked: items.length,
        categories,
      });
    } catch {
      setRiskBoard({
        items: [],
        criticalCount: 0,
        warningCount: 0,
        totalTracked: 0,
        categories: [],
      });
    }
  };

  useEffect(() => {
    if (activeTab === 'permissions' && canReadSystemSettings) {
      Promise.all([
        api.get<Permission[]>('/permissions'),
        api.get<Role[]>('/roles'),
        api.get<Array<{ Id: number; Name: string; Permissions: RolePermission[] }>>('/permissions/roles'),
      ]).then(([permRes, roleRes, matrixRes]) => {
        console.log('[useEffect] Loaded permissions:', permRes.data);
        console.log('[useEffect] Loaded roles:', roleRes.data);
        console.log('[useEffect] Loaded matrix:', matrixRes.data);
        setPermissions(permRes.data);
        setRoles(roleRes.data);
        const next: Record<number, RolePermission[]> = {};
        matrixRes.data.forEach((row: any) => {
          const roleId = row.Id ?? row.id;
          const permissionsList = row.Permissions ?? row.permissions ?? [];
          console.log(`[useEffect] Setting permissions for role ${roleId}:`, permissionsList);
          next[roleId] = permissionsList;
        });
        console.log('[useEffect] Loaded matrix sample role ids:', Object.keys(next));
        console.log('[useEffect] Final rolePermissions:', next);
        setRolePermissions(next);
        setPermissionsError(null);
        }).catch((err) => {
        console.error('[useEffect] Error loading permissions:', err);
        setPermissions([]);
        setRoles([]);
        setRolePermissions({});
        const status = err?.response?.status || err?.status || err?.cause?.status;
        if (status === 403) {
          setPermissionsError('Недостатньо прав для перегляду матриці. Перевір, що для ролі призначено permission page.admin.');
        } else if (status === 404) {
          setPermissionsError('API permissions не знайдено. Перевір, що бекенд працює на цьому ж хості.');
        } else {
          setPermissionsError('Не вдалося завантажити права. Перевір, що бекенд запущений.');
        }
      });
    }
    if (activeTab === 'print-settings' && canReadSystemSettings) {
      api.get<PrintSettings>('/permissions/print-settings')
        .then((res) => setPrintSettings(res.data))
        .catch((err) => {
          setPrintSettings(null);
          setPermissionsError(err?.response?.status === 404
            ? 'Налаштування друку ще не створені. Відкрий їх повторно після збереження або перезапусти бекенд.'
            : 'Не вдалося завантажити налаштування друку.');
        });
    }
    if (activeTab === 'google-document-ai' && canReadSystemSettings) {
      loadGoogleDocAiSettings();
    }
    if (activeTab === 'dish-analysis') {
      loadDishAnalysis();
    }
    if (activeTab === 'risk-board') {
      loadRiskBoard();
    }
  }, [activeTab, canReadSystemSettings]);

  const tabButtonStyle = (tab: AdminTab): React.CSSProperties => ({
    padding: '10px 14px',
    borderRadius: 'var(--crm-radius-md)',
    border: activeTab === tab ? '2px solid var(--crm-primary)' : '1px solid var(--crm-border)',
    background: activeTab === tab ? 'var(--crm-primary)' : 'var(--crm-surface-alt)',
    color: activeTab === tab ? 'var(--crm-primary-contrast)' : 'var(--crm-text)',
    cursor: 'pointer',
    fontWeight: 700,
    transition: 'all 0.2s ease',
  });

  const inputStyle: React.CSSProperties = {
    padding: '8px 10px',
    border: '1px solid var(--crm-border)',
    borderRadius: 'var(--crm-radius-sm)',
    fontSize: '13px',
    fontFamily: 'inherit',
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text)',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 14px',
    borderRadius: 'var(--crm-radius-sm)',
    background: 'var(--crm-success-bg)',
    color: 'var(--crm-success)',
    border: '1px solid var(--crm-success)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '13px',
    transition: 'background 0.2s ease',
  };

  const buttonDisabledStyle: React.CSSProperties = {
    ...buttonStyle,
    background: 'var(--crm-surface-alt)',
    color: 'var(--crm-text-muted)',
    border: '1px solid var(--crm-border)',
    cursor: 'not-allowed',
  };

  const renderPrintSettingsContent = (compact = false) => (
    <>
      <h3 style={{ marginTop: 0, marginBottom: '8px', color: 'var(--crm-text)' }}>Налаштування друку</h3>
      <p style={{ color: 'var(--crm-text-muted)', margin: compact ? '0 0 12px' : '0 0 16px' }}>
        Керуйте тим, як назви страв показуються в замовленнях та підготовчих листах.
      </p>
      {permissionsError && <div role="alert" style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-danger)' }}>{permissionsError}</div>}
      {printSettings ? (
        <div style={{ display: 'grid', gap: 12, maxWidth: compact ? '100%' : 500 }}>
          <div style={{ padding: '12px 14px', borderRadius: 'var(--crm-radius-md)', border: '1px solid var(--crm-border)', background: 'var(--crm-surface-alt)' }}>
            <div style={{ fontWeight: 700, color: 'var(--crm-text)', marginBottom: '8px' }}>Видима назва страви</div>
            <div style={{ display: 'grid', gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '13px' }}>Формат назви страви</span>
                <input
                  value={printSettings.dishNameFormat}
                  onChange={(e) => setPrintSettings({ ...printSettings, dishNameFormat: e.target.value })}
                  style={inputStyle}
                  placeholder="{name} ({output}; {servings})"
                />
                <span style={{ color: 'var(--crm-text-muted)', fontSize: '12px' }}>Доступні плейсхолдери: {'{name}'}, {'{output}'}, {'{servings}'}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={printSettings.appendOutputToDishName}
                  onChange={(e) => setPrintSettings({ ...printSettings, appendOutputToDishName: e.target.checked })}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '13px' }}>Додавати вихід до назви, якщо формат не спрацював</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={printSettings.appendServingsToDishName}
                  onChange={(e) => setPrintSettings({ ...printSettings, appendServingsToDishName: e.target.checked })}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '13px' }}>Додавати кількість порцій до назви, якщо формат не спрацював</span>
              </label>
            </div>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '13px' }}>Базовий розмір шрифту (px)</span>
            <input type="number" value={printSettings.baseFontSize} onChange={(e) => setPrintSettings({ ...printSettings, baseFontSize: Number(e.target.value) })} style={inputStyle} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '13px' }}>Розмір заголовка (px)</span>
            <input type="number" value={printSettings.titleFontSize} onChange={(e) => setPrintSettings({ ...printSettings, titleFontSize: Number(e.target.value) })} style={inputStyle} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '13px' }}>Малий розмір шрифту (px)</span>
            <input type="number" value={printSettings.smallFontSize} onChange={(e) => setPrintSettings({ ...printSettings, smallFontSize: Number(e.target.value) })} style={inputStyle} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '13px' }}>Padding комірок (px)</span>
            <input type="number" value={printSettings.cellPadding} onChange={(e) => setPrintSettings({ ...printSettings, cellPadding: Number(e.target.value) })} style={inputStyle} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '13px' }}>Відступ між секціями (px)</span>
            <input type="number" value={printSettings.sectionGap} onChange={(e) => setPrintSettings({ ...printSettings, sectionGap: Number(e.target.value) })} style={inputStyle} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '13px' }}>Поле сторінки (px)</span>
            <input type="number" value={printSettings.pageMargin} onChange={(e) => setPrintSettings({ ...printSettings, pageMargin: Number(e.target.value) })} style={inputStyle} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '13px' }}>Шрифт нотаток</span>
            <input value={printSettings.noteFontFamily} onChange={(e) => setPrintSettings({ ...printSettings, noteFontFamily: e.target.value })} style={inputStyle} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={printSettings.printBackgrounds} onChange={(e) => setPrintSettings({ ...printSettings, printBackgrounds: e.target.checked })} style={{ width: '16px', height: '16px' }} />
            <span style={{ fontWeight: 600, color: 'var(--crm-text)', fontSize: '13px' }}>Друкувати фони</span>
          </label>
          <button type="button" onClick={savePrintSettings} disabled={savingPrintSettings} style={savingPrintSettings ? buttonDisabledStyle : buttonStyle}>
            {savingPrintSettings ? '💾 Збереження…' : '💾 Зберегти налаштування'}
          </button>
        </div>
      ) : <div style={{ padding: '12px', background: 'var(--crm-surface-alt)', borderRadius: 'var(--crm-radius-sm)', color: 'var(--crm-text-muted)' }}>Налаштування не завантажені</div>}
    </>
  );

  return (
    <div className="page-shell page-shell--wide">
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>Адмін панель</h2>
      <p>
        <Link to="/admin/employees">Облікові записи, паролі та активність працівників</Link>
      </p>
      <p>
        <Link to="/management/sale-catalog">Затвердження позицій для продажу</Link>
      </p>
      {accessError && <p role="alert">{accessError}</p>}
      {allGroupsOverview
        ? <p role="status">Режим огляду всіх груп: системні налаштування доступні лише для перегляду.</p>
        : !canEditAdminSettings && <p role="status">Режим перегляду: для зміни налаштувань потрібне право «Редагування» для адмін панелі.</p>}

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', margin: '16px 0 22px' }}>
        <button type="button" style={tabButtonStyle('permissions')} onClick={() => setActiveTab('permissions')}>Права</button>
        <button type="button" style={tabButtonStyle('print-settings')} onClick={() => setActiveTab('print-settings')}>Друк</button>
        <button type="button" style={tabButtonStyle('notifications')} onClick={() => setActiveTab('notifications')}>Сповіщення</button>
        <button type="button" style={tabButtonStyle('price-recalc')} onClick={() => setActiveTab('price-recalc')}>Ціни з накладних</button>
        <button type="button" style={tabButtonStyle('dish-analysis')} onClick={() => setActiveTab('dish-analysis')}>Аналіз калькуляцій</button>
        <button type="button" style={tabButtonStyle('risk-board')} onClick={() => setActiveTab('risk-board')}>Ризики складу</button>
        <button type="button" style={tabButtonStyle('template')} onClick={() => setActiveTab('template')}>Шаблон страви</button>
        <button type="button" style={tabButtonStyle('google-document-ai')} onClick={() => setActiveTab('google-document-ai')}>Google Document AI</button>
        <button type="button" style={tabButtonStyle('ai-assistant')} onClick={() => setActiveTab('ai-assistant')}>ШІ-помічник</button>
        <button type="button" style={tabButtonStyle('structure')} onClick={() => setActiveTab('structure')}>Структура та склади</button>
        <button type="button" style={tabButtonStyle('producing-kitchens')} onClick={() => setActiveTab('producing-kitchens')}>Виробничі кухні</button>
      </div>

      <fieldset disabled={!canEditAdminSettings} aria-label="Налаштування адміністрування" style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {activeTab === 'ai-assistant' && (canReadSystemSettings
        ? <AiAssistantSettings canEdit={canEditSystemSettings} />
        : <p role="status">Налаштування ШІ доступні лише системному адміністратору.</p>)}
      {activeTab === 'structure' && <OperatingGroupsSettings canEdit={canEditAdminSettings} />}
      {activeTab === 'producing-kitchens' && <ProducingKitchensSettings canEdit={canEditAdminSettings} />}
      {activeTab === 'permissions' && canReadSystemSettings && (
        <fieldset disabled={!canEditSystemSettings} aria-label="Глобальні права доступу">
        <section style={printSettingsCardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '8px', color: 'var(--crm-text)' }}>Матриця прав доступу</h3>
          <p style={{ color: 'var(--crm-text-muted)', marginBottom: '16px' }}>Виберіть роль і встановіть права: перегляд, редагування, друк, імпорт, експорт.</p>
          <p style={{ color: 'var(--crm-text-muted)' }}>
            Для керування обліковими записами й налаштуваннями потрібні «Перегляд» і «Редагування» для <code>page.admin</code>.
            Зняття дозволу не відновлюється після перезапуску. Інші модулі ще проходять перевірку відповідності прапорцям дій.
          </p>
          {permissionsMessage && <p role="status">{permissionsMessage}</p>}
          {permissionsError && <div role="alert" style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-danger)' }}>{permissionsError}</div>}
          
          {/* Role selector and management */}
          <div style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', padding: '16px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', marginBottom: '20px' }}>
            <div className="admin-role-management-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              {/* Role selector */}
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: 'var(--crm-text)', marginBottom: '8px', fontSize: '13px' }}>Виберіть роль:</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Пошук ролі..."
                    value={roleSearchQuery}
                    onChange={(e) => setRoleSearchQuery(e.target.value)}
                    style={{ ...inputStyle, width: '100%', marginBottom: '8px' }}
                  />
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-surface)' }}>
                    {roles
                      .filter((r) => r.name.toLowerCase().includes(roleSearchQuery.toLowerCase()))
                      .map((role) => (
                        <div
                          key={role.id}
                          onClick={() => {
                            setSelectedRoleId(role.id);
                            setRoleSearchQuery('');
                            setEditingRoleId(null);
                            setPermissionsError(null);
                          }}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            background: selectedRoleId === role.id ? 'var(--crm-info-bg)' : 'var(--crm-surface)',
                            borderBottom: '1px solid var(--crm-table-row-border)',
                            fontWeight: selectedRoleId === role.id ? 600 : 400,
                            color: selectedRoleId === role.id ? 'var(--crm-info)' : 'var(--crm-text)',
                            transition: 'background 0.2s ease',
                          }}
                        >
                          {role.name}
                          {role.description && <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)', marginTop: '2px' }}>{role.description}</div>}
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              {/* Create new role */}
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: 'var(--crm-text)', marginBottom: '8px', fontSize: '13px' }}>Створити нову роль:</label>
                {!showNewRoleForm ? (
                  <button
                    type="button"
                    onClick={() => setShowNewRoleForm(true)}
                    style={{ ...buttonStyle, width: '100%' }}
                  >
                    + Додати нову роль
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Назва ролі"
                      value={newRole.name}
                      onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                      style={inputStyle}
                      autoFocus
                    />
                    <input
                      type="text"
                      placeholder="Опис (необовʼязково)"
                      value={newRole.description}
                      onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                      style={inputStyle}
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={createNewRole}
                        disabled={savingNewRole || !newRole.name.trim()}
                        style={savingNewRole || !newRole.name.trim() ? buttonDisabledStyle : buttonStyle}
                      >
                        {savingNewRole ? 'Створення…' : '✓ Створити'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewRoleForm(false);
                          setNewRole({ name: '', description: '' });
                        }}
                        style={{ ...buttonStyle, background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderColor: 'var(--crm-border)' }}
                      >
                        ✕ Скасувати
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Permissions matrix for selected role */}
          {selectedRoleId !== null && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '16px' }}>
                {editingRoleId === selectedRoleId ? (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1 }}>
                    <input
                      type="text"
                      value={editingRole.name}
                      onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                      style={{ ...inputStyle, flex: 1 }}
                      autoFocus
                    />
                    <input
                      type="text"
                      placeholder="Опис"
                      value={editingRole.description}
                      onChange={(e) => setEditingRole({ ...editingRole, description: e.target.value })}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => updateRole(selectedRoleId)}
                      disabled={savingEditRole || !editingRole.name.trim()}
                      style={savingEditRole || !editingRole.name.trim() ? buttonDisabledStyle : buttonStyle}
                    >
                      {savingEditRole ? '💾…' : '✓'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingRoleId(null);
                        setEditingRole({ name: '', description: '' });
                      }}
                      style={{ ...buttonStyle, background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderColor: 'var(--crm-border)', padding: '8px 12px' }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <>
                    <h4 style={{ margin: 0, color: 'var(--crm-text)' }}>
                      Права для ролі: <strong>{roles.find((r) => r.id === selectedRoleId)?.name}</strong>
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        const role = roles.find((r) => r.id === selectedRoleId);
                        if (role) {
                          setEditingRoleId(selectedRoleId);
                          setEditingRole({ name: role.name, description: role.description || '' });
                        }
                      }}
                      style={{ ...buttonStyle, background: 'var(--crm-info-bg)', color: 'var(--crm-info)', borderColor: 'var(--crm-info)', padding: '8px 12px', whiteSpace: 'nowrap' }}
                    >
                      ✎ Редагувати
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => saveRolePermissions(selectedRoleId)}
                  disabled={savingRoleId === selectedRoleId}
                  style={savingRoleId === selectedRoleId ? buttonDisabledStyle : buttonStyle}
                >
                  {savingRoleId === selectedRoleId ? '💾 Збереження…' : '💾 Зберегти права'}
                </button>
              </div>

              <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: 'var(--crm-surface-alt)', borderBottom: '2px solid var(--crm-border)' }}>
                      <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, color: 'var(--crm-text)', minWidth: '250px' }}>Право</th>
                      <th style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 600, color: 'var(--crm-text)', minWidth: '120px' }}>Перегляд</th>
                      <th style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 600, color: 'var(--crm-text)', minWidth: '120px' }}>Редагування</th>
                      <th style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 600, color: 'var(--crm-text)', minWidth: '120px' }}>Друк</th>
                      <th style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 600, color: 'var(--crm-text)', minWidth: '120px' }}>Імпорт</th>
                      <th style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 600, color: 'var(--crm-text)', minWidth: '120px' }}>Експорт</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissions.map((permission, idx) => {
                      const current = rolePermissions[selectedRoleId]?.find((p) => p.permissionId === permission.id);
                      console.log(`Permission ${permission.id} (${permission.name}):`, current);
                      return (
                        <tr key={permission.id} style={{ borderBottom: '1px solid var(--crm-table-row-border)', background: idx % 2 === 0 ? 'var(--crm-surface)' : 'var(--crm-surface-alt)', color: 'var(--crm-text)' }}>
                          <td style={{ padding: '10px 12px' }}>
                            <strong style={{ color: 'var(--crm-text)' }}>{permission.name}</strong>
                            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginTop: '2px' }}>
                              {permission.category} • <code style={{ background: 'var(--crm-input-bg-muted)', color: 'var(--crm-text)', padding: '1px 4px', borderRadius: '3px' }}>{permission.key}</code>
                            </div>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={current?.canView ?? false}
                              onChange={(e) => updateRolePermission(selectedRoleId, permission.id, 'canView', e.target.checked)}
                              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={current?.canEdit ?? false}
                              onChange={(e) => updateRolePermission(selectedRoleId, permission.id, 'canEdit', e.target.checked)}
                              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={current?.canPrint ?? false}
                              onChange={(e) => updateRolePermission(selectedRoleId, permission.id, 'canPrint', e.target.checked)}
                              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={current?.canImport ?? false}
                              onChange={(e) => updateRolePermission(selectedRoleId, permission.id, 'canImport', e.target.checked)}
                              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={current?.canExport ?? false}
                              onChange={(e) => updateRolePermission(selectedRoleId, permission.id, 'canExport', e.target.checked)}
                              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                Всього прав: <strong>{permissions.length}</strong>
              </div>
            </div>
          )}

          {selectedRoleId === null && (
            <div style={{ padding: '20px', textAlign: 'center', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', color: 'var(--crm-text-muted)' }}>
              Виберіть роль зі списку, щоб встановити права доступу
            </div>
          )}

          {/* Add new permission section */}
          <div style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', padding: '14px', borderRadius: 'var(--crm-radius-sm)', marginTop: '20px' }}>
            <h4 style={{ marginTop: 0, marginBottom: '12px', fontSize: '13px', color: 'var(--crm-text)' }}>Додати нове право</h4>
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 12 }}>
              <input placeholder="Key (напр. page.admin)" value={newPermission.key} onChange={(e) => setNewPermission({ ...newPermission, key: e.target.value })} style={inputStyle} />
              <input placeholder="Name (напр. Адмін панель)" value={newPermission.name} onChange={(e) => setNewPermission({ ...newPermission, name: e.target.value })} style={inputStyle} />
              <input placeholder="Category (напр. Сторінки)" value={newPermission.category} onChange={(e) => setNewPermission({ ...newPermission, category: e.target.value })} style={inputStyle} />
              <input placeholder="Description" value={newPermission.description} onChange={(e) => setNewPermission({ ...newPermission, description: e.target.value })} style={inputStyle} />
            </div>
            <button type="button" onClick={createPermission} disabled={savingPermission || !newPermission.key || !newPermission.name} style={savingPermission || !newPermission.key || !newPermission.name ? buttonDisabledStyle : buttonStyle}>
              {savingPermission ? 'Створення…' : '+ Створити право'}
            </button>
          </div>
        </section>
        </fieldset>
      )}
      {activeTab === 'permissions' && !canReadSystemSettings &&
        <p role="status">Керування ролями та правами доступне лише системному адміністратору.</p>}

      {activeTab === 'print-settings' && (
        canReadSystemSettings
          ? <fieldset disabled={!canEditSystemSettings} aria-label="Глобальні налаштування друку">
        <section style={printSettingsCardStyle}>
          {renderPrintSettingsContent()}
        </section>
            </fieldset>
          : <p role="status">Налаштування друку доступні лише системному адміністратору.</p>
      )}

      {activeTab === 'notifications' && (
        <section style={printSettingsCardStyle}>
          {canReadSystemSettings
            ? <RestaurantNotificationSettings canEdit={canEditSystemSettings} />
            : <RestaurantNotificationStatus />}
        </section>
      )}

{activeTab === 'google-document-ai' && (canReadSystemSettings ? (
        <fieldset disabled={!canEditSystemSettings} aria-label="Глобальні налаштування Google Document AI">
        <section style={printSettingsCardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '8px', color: 'var(--crm-text)' }}>Google Document AI (розпізнавання фото накладних)</h3>
          <p style={{ color: 'var(--crm-text-muted)', marginBottom: '12px' }}>
            Фото накладних безпечно надсилаються на сервер, а сервер обробляє їх через сервіс Google Cloud Document AI.
            Обліковий запис сервісу (JSON-ключ) зберігається лише на сервері поза кодовою базою і ніколи не показується повторно після збереження.
          </p>
          <div style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 16, marginBottom: 16 }}>
            <h4 style={{ marginTop: 0, marginBottom: 8, color: 'var(--crm-text)' }}>Як налаштувати</h4>
            <ol style={{ margin: 0, paddingLeft: 20, color: 'var(--crm-text)', fontSize: 13, lineHeight: 1.6 }}>
              <li>У Google Cloud Console створіть/оберіть проєкт та увімкніть Document AI API.</li>
              <li>Створіть процесор типу <strong>Invoice Parser</strong>, скопіюйте Project ID, Location (наприклад <code>us</code> або <code>eu</code>) та Processor ID.</li>
              <li>Створіть сервісний акаунт з роллю <strong>Document AI API User</strong>, згенеруйте JSON-ключ.</li>
              <li>Введіть Project ID / Location / Processor ID нижче, вставте вміст JSON-ключа або завантажте файл, і натисніть «Зберегти».</li>
            </ol>
          </div>

          {googleDocAiError && <div role="alert" style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-danger)' }}>{googleDocAiError}</div>}
          {googleDocAiMessage && <div role="status" style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--crm-success-bg)', color: 'var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-success)' }}>{googleDocAiMessage}</div>}

          {googleDocAiSettings && (
            <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--crm-text)' }}>
              <div><strong>Статус облікових даних:</strong> {googleDocAiSettings.credentialConfigured ? '✅ Налаштовано' : '⚠️ Не налаштовано'} — {googleDocAiSettings.credentialSource}</div>
              {googleDocAiSettings.statusMessage && <div style={{ color: 'var(--crm-text-muted)', marginTop: 4 }}>{googleDocAiSettings.statusMessage}</div>}
            </div>
          )}

          <div style={{ display: 'grid', gap: 12, maxWidth: 640 }}>
            <label style={{ display: 'grid', gap: 4, fontWeight: 600, color: 'var(--crm-text)', fontSize: 13 }}>
              Project ID
              <input value={googleDocAiForm.projectId} onChange={(e) => setGoogleDocAiForm({ ...googleDocAiForm, projectId: e.target.value })} style={inputStyle} placeholder="my-gcp-project" />
            </label>
            <label style={{ display: 'grid', gap: 4, fontWeight: 600, color: 'var(--crm-text)', fontSize: 13 }}>
              Location
              <input value={googleDocAiForm.location} onChange={(e) => setGoogleDocAiForm({ ...googleDocAiForm, location: e.target.value })} style={inputStyle} placeholder="us" />
            </label>
            <label style={{ display: 'grid', gap: 4, fontWeight: 600, color: 'var(--crm-text)', fontSize: 13 }}>
              Processor ID
              <input value={googleDocAiForm.processorId} onChange={(e) => setGoogleDocAiForm({ ...googleDocAiForm, processorId: e.target.value })} style={inputStyle} placeholder="abcdef0123456789" />
            </label>

            <div style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 16 }}>
              <h4 style={{ marginTop: 0, marginBottom: 8, color: 'var(--crm-text)' }}>Обліковий запис сервісу (JSON-ключ)</h4>
              <p style={{ color: 'var(--crm-text-muted)', fontSize: 12, marginTop: 0, marginBottom: 10 }}>
                Залиште порожнім, щоб зберегти поточні облікові дані без змін. Вставте JSON нижче або оберіть файл ключа — вміст читається лише у формі в браузері та надсилається один раз під час збереження.
              </p>
              <textarea
                value={googleDocAiForm.credentialJson}
                onChange={(e) => setGoogleDocAiForm({ ...googleDocAiForm, credentialJson: e.target.value })}
                placeholder='{"type": "service_account", "project_id": "...", "client_email": "...", "private_key": "..."}'
                rows={4}
                style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
              />
              <input
                type="file"
                accept="application/json,.json"
                style={{ marginTop: 8 }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleGoogleDocAiCredentialFile(file);
                  e.target.value = '';
                }}
              />
            </div>

            <button type="button" onClick={saveGoogleDocAiSettings} disabled={savingGoogleDocAi} style={savingGoogleDocAi ? buttonDisabledStyle : buttonStyle}>
              {savingGoogleDocAi ? '💾 Збереження…' : '💾 Зберегти налаштування'}
            </button>

            <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: 12, marginTop: 4 }}>
              {!googleDocAiClearConfirm ? (
                <button
                  type="button"
                  onClick={() => setGoogleDocAiClearConfirm(true)}
                  disabled={savingGoogleDocAi || !googleDocAiSettings?.credentialConfigured}
                  style={{ padding: '8px 14px', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-danger)', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', cursor: (savingGoogleDocAi || !googleDocAiSettings?.credentialConfigured) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px' }}
                >
                  🗑️ Видалити збережені облікові дані
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--crm-danger)', fontWeight: 600, fontSize: 13 }}>Підтвердіть видалення збереженого JSON-ключа сервісного акаунта?</span>
                  <button type="button" onClick={clearGoogleDocAiCredential} disabled={savingGoogleDocAi} style={{ padding: '6px 12px', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-danger)', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Так, видалити</button>
                  <button type="button" onClick={() => setGoogleDocAiClearConfirm(false)} disabled={savingGoogleDocAi} style={{ padding: '6px 12px', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-border)', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Скасувати</button>
                </div>
              )}
            </div>
          </div>
        </section>
        </fieldset>
      ) : <p role="status">Налаштування Document AI доступні лише системному адміністратору.</p>)}

      {activeTab === 'price-recalc' && (
        <section style={printSettingsCardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '8px', color: 'var(--crm-text)' }}>💰 Перерахунок цін з накладних</h3>
          <p style={{ color: 'var(--crm-text-muted)', marginBottom: 16 }}>
            Обчислює середню ціну товарів на основі останніх N прихідних накладних з урахуванням ПДВ постачальника.
            Спочатку перегляньте зміни — потім застосуйте.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--crm-text)' }}>
              Кількість останніх накладних
              <input type="number" min={1} max={20} value={priceRecalcLastN}
                onChange={e => setPriceRecalcLastN(Number(e.target.value))}
                style={{ ...inputStyle, width: 80 }} />
            </label>
            <button type="button" onClick={loadPriceRecalcPreview} disabled={priceRecalcLoading}
              style={{ ...buttonStyle, background: 'var(--crm-info-bg)', color: 'var(--crm-info)', borderColor: 'var(--crm-info)' }}>
              {priceRecalcLoading ? '…' : '🔍 Переглянути зміни'}
            </button>
            {priceRecalcPreview && priceRecalcPreview.filter((i: any) => i.hasChange).length > 0 && (
              <button type="button" onClick={applyPriceRecalc} disabled={priceRecalcLoading}
                style={{ ...buttonStyle, background: 'var(--crm-warning-bg)', color: 'var(--crm-warning)', borderColor: 'var(--crm-warning)' }}>
                ⚡ Застосувати ({priceRecalcPreview.filter((i: any) => i.hasChange).length} товарів)
              </button>
            )}
          </div>
          {priceRecalcStatus && (
            <div role="status" style={{ marginBottom: 16, padding: 12, background: 'var(--crm-success-bg)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', color: 'var(--crm-success)' }}>
              {priceRecalcStatus}
            </div>
          )}
          {priceRecalcPreview && (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ marginBottom: 8, fontSize: 13, color: 'var(--crm-text)' }}>
                Знайдено змін: <b style={{ color: 'var(--crm-warning)' }}>{priceRecalcPreview.filter((i: any) => i.hasChange).length}</b> з {priceRecalcPreview.length} товарів
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left' }}>Товар</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Поточна ціна</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Нова ціна</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Δ</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center' }}>Накладних</th>
                  </tr>
                </thead>
                <tbody>
                  {priceRecalcPreview.filter((i: any) => i.hasChange).map((item: any) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid var(--crm-table-row-border)', background: 'var(--crm-warning-bg)', color: 'var(--crm-text)' }}>
                      <td style={{ padding: '7px 10px', fontWeight: 500 }}>{item.name}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--crm-text-muted)' }}>{Number(item.currentPrice).toFixed(4)} грн</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--crm-warning)', fontWeight: 600 }}>{Number(item.newPrice).toFixed(4)} грн</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', color: item.newPrice > item.currentPrice ? 'var(--crm-danger)' : 'var(--crm-success)' }}>
                        {item.newPrice > item.currentPrice ? '▲' : '▼'} {Math.abs(item.newPrice - item.currentPrice).toFixed(4)}
                      </td>
                      <td style={{ padding: '7px 10px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>{item.receiptsUsed}</td>
                    </tr>
                  ))}
                  {priceRecalcPreview.filter((i: any) => !i.hasChange).length > 0 && (
                    <tr>
                      <td colSpan={5} style={{ padding: '8px 10px', color: 'var(--crm-text-muted)', fontSize: 12, textAlign: 'center' }}>
                        + {priceRecalcPreview.filter((i: any) => !i.hasChange).length} товарів без змін
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {activeTab === 'dish-analysis' && (
        <section style={printSettingsCardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '8px', color: 'var(--crm-text)' }}>Аналіз калькуляцій і продажів</h3>
          {dishAnalysis ? (
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                <div style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 14 }}>
                  <div style={{ color: 'var(--crm-text-muted)', fontSize: 12, marginBottom: 4 }}>Страв</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--crm-text)' }}>{dishAnalysis.total}</div>
                </div>
                <div style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 14 }}>
                  <div style={{ color: 'var(--crm-text-muted)', fontSize: 12, marginBottom: 4 }}>Середня маржа</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--crm-text)' }}>{dishAnalysis.avgMargin.toFixed(1)}%</div>
                </div>
                <div style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 14 }}>
                  <div style={{ color: 'var(--crm-text-muted)', fontSize: 12, marginBottom: 4 }}>Виторг</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--crm-text)' }}>{(dishAnalysis.sales?.totalRevenue ?? 0).toFixed(0)} грн</div>
                </div>
                <div style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 14 }}>
                  <div style={{ color: 'var(--crm-text-muted)', fontSize: 12, marginBottom: 4 }}>Замовлень</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--crm-text)' }}>{dishAnalysis.sales?.totalOrders ?? 0}</div>
                </div>
              </div>

              <div style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 16 }}>
                <h4 style={{ marginTop: 0, marginBottom: 12, color: 'var(--crm-text)' }}>Рекомендації</h4>
                <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--crm-text)', lineHeight: 1.8 }}>
                  {dishAnalysis.recommendations.map((item: string, index: number) => <li key={index}>{item}</li>)}
                </ul>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
                <div style={{ background: 'var(--crm-success-bg)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-md)', padding: 16 }}>
                  <h4 style={{ marginTop: 0, marginBottom: 12, color: 'var(--crm-success)' }}>Продажі</h4>
                  <div style={{ color: 'var(--crm-text)', lineHeight: 1.8 }}>
                    <div>Середній чек: <strong>{(dishAnalysis.sales?.avgOrderValue ?? 0).toFixed(0)} грн</strong></div>
                    <div>Продано одиниць: <strong>{dishAnalysis.sales?.totalUnits ?? 0}</strong></div>
                    {dishAnalysis.sales?.bestItem ? (
                      <div>Лідер продажів: <strong>{dishAnalysis.sales.bestItem.name}</strong></div>
                    ) : <div>Лідер продажів: <strong>немає даних</strong></div>}
                  </div>
                </div>

                {dishAnalysis.sales?.topItems && dishAnalysis.sales.topItems.length > 0 && (
                  <div style={{ background: 'var(--crm-info-bg)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-md)', padding: 16 }}>
                    <h4 style={{ marginTop: 0, marginBottom: 12, color: 'var(--crm-info)' }}>Топ позицій</h4>
                    <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--crm-text)', lineHeight: 1.8 }}>
                      {dishAnalysis.sales.topItems.map((item: any) => (
                        <li key={item.name}>{item.name} — {item.quantity} шт. / {item.revenue.toFixed(0)} грн</li>
                      ))}
                    </ul>
                  </div>
                )}

              </div>

              {dishAnalysis.lowMargin.length > 0 && (
                <div style={{ background: 'var(--crm-danger-bg)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-md)', padding: 16 }}>
                  <h4 style={{ marginTop: 0, marginBottom: 12, color: 'var(--crm-danger)' }}>Низька маржа</h4>
                  <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7, color: 'var(--crm-text)' }}>
                    {dishAnalysis.lowMargin.map((item: any) => <li key={item.id}>{item.name} — {item.marginPct.toFixed(1)}%</li>)}
                  </ul>
                </div>
              )}

              {dishAnalysis.expensive.length > 0 && (
                <div style={{ background: 'var(--crm-warning-bg)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-md)', padding: 16 }}>
                  <h4 style={{ marginTop: 0, marginBottom: 12, color: 'var(--crm-warning)' }}>Висока собівартість</h4>
                  <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7, color: 'var(--crm-text)' }}>
                    {dishAnalysis.expensive.map((item: any) => <li key={item.id}>{item.name} — {((item.cost / item.price) * 100).toFixed(0)}% витрат до ціни</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: '12px', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', color: 'var(--crm-text-muted)' }}>Завантаження аналізу калькуляцій…</div>
          )}
        </section>
      )}

      {activeTab === 'risk-board' && (
        <section style={printSettingsCardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '8px', color: 'var(--crm-text)' }}>Ризики складу</h3>
          <p style={{ color: 'var(--crm-text-muted)', marginBottom: '16px' }}>
            Автоматичний огляд проблемних позицій на основі поточних залишків. Дані лише для читання.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ background: 'var(--crm-danger-bg)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-md)', padding: 14 }}>
              <div style={{ color: 'var(--crm-danger)', fontSize: 12, marginBottom: 4 }}>Критичні</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--crm-danger)' }}>{riskBoard.criticalCount}</div>
            </div>
            <div style={{ background: 'var(--crm-warning-bg)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-md)', padding: 14 }}>
              <div style={{ color: 'var(--crm-warning)', fontSize: 12, marginBottom: 4 }}>Потребують уваги</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--crm-warning)' }}>{riskBoard.warningCount}</div>
            </div>
            <div style={{ background: 'var(--crm-info-bg)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-md)', padding: 14 }}>
              <div style={{ color: 'var(--crm-info)', fontSize: 12, marginBottom: 4 }}>Позицій у контролі</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--crm-info)' }}>{riskBoard.totalTracked}</div>
            </div>
          </div>

          <div className="admin-risk-board-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(260px, 1fr)', gap: 16 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)', color: 'var(--crm-text)' }}>
                <thead>
                  <tr style={{ background: 'var(--crm-surface-alt)' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid var(--crm-border)' }}>Продукт</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid var(--crm-border)' }}>Категорія</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid var(--crm-border)' }}>Залишок</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid var(--crm-border)' }}>Прихід</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid var(--crm-border)' }}>Витрата</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid var(--crm-border)' }}>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {riskBoard.items.map((item) => (
                    <tr key={item.productId} style={{ borderBottom: '1px solid var(--crm-table-row-border)' }}>
                      <td style={{ padding: '8px 12px' }}>{item.productName}</td>
                      <td style={{ padding: '8px 12px' }}>{item.categoryName || 'Без категорії'}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: item.riskLevel === 'critical' ? 'var(--crm-danger)' : item.riskLevel === 'warning' ? 'var(--crm-warning)' : 'var(--crm-success)' }}>
                        {item.balance.toFixed(2)}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>{item.stockIn.toFixed(2)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>{item.stockOut.toFixed(2)}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: 999,
                          fontSize: 12,
                          background: item.riskLevel === 'critical' ? 'var(--crm-danger-bg)' : item.riskLevel === 'warning' ? 'var(--crm-warning-bg)' : 'var(--crm-success-bg)',
                          color: item.riskLevel === 'critical' ? 'var(--crm-danger)' : item.riskLevel === 'warning' ? 'var(--crm-warning)' : 'var(--crm-success)',
                        }}>
                          {item.riskLabel}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {riskBoard.items.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ padding: '16px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>
                        Немає ризикових позицій або дані ще не завантажені.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
              <div style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 14 }}>
                <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--crm-text)' }}>Під контролем</div>
                <div style={{ color: 'var(--crm-text-muted)', lineHeight: 1.7 }}>
                  {riskBoard.categories.length > 0 ? riskBoard.categories.join(', ') : 'Категорії ще не визначені'}
                </div>
              </div>
              <div style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 14 }}>
                <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--crm-text)' }}>Що це дає</div>
                <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--crm-text)', lineHeight: 1.7 }}>
                  <li>видно від’ємні залишки одразу</li>
                  <li>можна швидше знайти позиції з витратою без приходу</li>
                  <li>менше ручного обходу інвентаря</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {activeTab === 'template' && (
        <section style={printSettingsCardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '8px', color: 'var(--crm-text)' }}>Шаблон нової страви</h3>
          <p style={{ color: 'var(--crm-text-muted)', marginBottom: '16px' }}>
            Експортуйте AI-ready шаблон Excel з інструкцією, довідником і листами для повноцінної калькуляції.
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button 
              type="button" 
              onClick={exportDishTemplate}
              style={buttonStyle}
            >
              📥 Експортувати AI-шаблон
            </button>
            <button
              type="button"
              onClick={exportSemiFinishedTemplate}
              style={buttonStyle}
            >
              📥 Експортувати AI-шаблон Н/Ф
            </button>
            <button 
              type="button" 
              onClick={() => alert('Після заповнення завантажте файл через сторінку Експорт/Імпорт -> Страви')}
              style={buttonStyle}
            >
              📤 Імпортувати з файлу
            </button>
          </div>
          <div style={{ marginTop: '20px', padding: '14px', background: 'var(--crm-surface-alt)', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-border)' }}>
            <h4 style={{ marginTop: 0, marginBottom: '10px', fontSize: '13px', color: 'var(--crm-text)' }}>Структура шаблону:</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: 'var(--crm-text)', lineHeight: '1.6' }}>
              <li><strong>Prompt</strong> — інструкція для іншого ШІ</li>
              <li><strong>Довідник</strong> — товари, напівфабрикати та операції з БД; Н/Ф можна вкладати в інші страви</li>
              <li><strong>Dish</strong> — основа нової калькуляції</li>
              <li><strong>Ingredients</strong> — склад і кількості</li>
              <li><strong>Steps</strong> — технологічні кроки</li>
              <li><strong>Окремий Н/Ф-шаблон</strong> — експортується окремою кнопкою для створення напівфабрикату перед використанням у страві</li>
            </ul>
          </div>
          <div style={{ marginTop: '16px', padding: '14px', background: 'var(--crm-success-bg)', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-success)' }}>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--crm-success)' }}>
              💡 <strong>Порада:</strong> Після експорту шаблону заповніть дані і завантажте файл через сторінку <strong>Експорт/Імпорт</strong> у розділі Страви.
            </p>
          </div>
        </section>
      )}
      </fieldset>
    </div>
  );
};

export default AdminDashboard;
